import { LangGraphTokenManager, langGraphTokenManager } from "./LangGraphTokenManager";

// Direct REST client for the fms-agent LangGraph deployment. Deliberately
// not @langchain/langgraph-sdk: the toolchain is TS 4.3 and the endpoints
// below are small and stable, so plain fetch avoids dependency and
// type-resolution risk. Auth is a Bearer JWT from the token manager; the
// deployment's auth layer (fms-agent app/auth.py) stamps and filters thread
// ownership server-side, so no user identifiers are sent here.
// API reference: https://docs.langchain.com/langsmith/agent-server

export type ChatRole = "human" | "ai";

export interface ChatMessage {
    role: ChatRole;
    content: string;
}

export interface ChatThreadSummary {
    threadId: string;
    createdAt: string;
    // Derived from the thread's first question; null for threads with no
    // usable transcript (created but never run, or unreadable state).
    title: string | null;
}

export interface StreamHandlers {
    onNodeUpdate: (nodeName: string) => void;
    onThinking: (step: string, delta: string) => void;
    onDone: () => void | Promise<void>;
    onError: (error: Error) => void;
}

export interface SseFrame {
    event: string | null;
    data: string | null;
}

// Class + DI + singleton (see CLAUDE.md "Front-end Architecture Standards").
// Deps injected with defaults; tests construct with a mock token manager +
// fetch. The pure data-shaping helpers below stay module-level exports —
// they are reused across layers (the converter uses formatThreadTitle) and
// have no IO/React/state.
export class ChatDataStore {
    constructor(
        private readonly tokenManager: LangGraphTokenManager = langGraphTokenManager,
        // Wrapper, not a bare `fetch` reference: calling `this.fetchFn(...)`
        // on a stored native `fetch` invokes it with `this` = this instance,
        // which the browser rejects ("Illegal invocation"). The arrow calls
        // bare `fetch`, preserving the global receiver.
        private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
        private readonly deploymentUrl: string = process.env.REACT_APP_FMS_DEPLOYMENT_URL ?? "",
        private readonly assistantId: string = "fms"
    ) {}

    // Bearer-authenticated fetch with exactly one 401 retry: the first 401
    // invalidates the cached token, mints fresh, and retries; a second 401
    // surfaces to the caller (the session is genuinely not authorized).
    private async authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
        // Guard the unconfigured case: without a deployment URL, requests would
        // go same-origin with the fms JWT attached and surface as confusing failures.
        if (!this.deploymentUrl) {
            throw new Error("Chat is not configured (missing REACT_APP_FMS_DEPLOYMENT_URL)");
        }
        const doFetch = async (token: string): Promise<Response> => this.fetchFn(this.deploymentUrl + path, {
            ...init,
            headers: {
                ...(init.headers ?? {}),
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });
        let response = await doFetch(await this.tokenManager.getToken());
        if (response.status === 401) {
            this.tokenManager.invalidate();
            response = await doFetch(await this.tokenManager.getToken());
        }
        return response;
    }

    public async createThread(): Promise<string> {
        // No metadata in the body: the deployment's auth layer stamps
        // metadata.owner from the verified JWT.
        const response = await this.authorizedFetch("/threads", { method: "POST", body: "{}" });
        if (!response.ok) {
            throw new Error("Failed to start a conversation");
        }
        const body = await response.json();
        return body.thread_id;
    }

    public async listThreads(): Promise<ChatThreadSummary[]> {
        // The platform owner-filters server-side; the client only pages.
        const response = await this.authorizedFetch("/threads/search", {
            method: "POST",
            body: JSON.stringify({ limit: 30 })
        });
        if (!response.ok) {
            throw new Error("Failed to load conversations");
        }
        const threads: { thread_id: string; created_at: string; metadata?: { title?: unknown }; values?: unknown }[] = await response.json();
        return threads
            .map(t => ({
                threadId: t.thread_id,
                createdAt: t.created_at,
                // A custom title (set via Rename, stored in thread metadata) wins
                // over the title derived from the thread's first question.
                title: typeof t.metadata?.title === "string" ? t.metadata.title : deriveThreadTitle(t.values)
            }))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    public async getThreadMessages(threadId: string): Promise<ChatMessage[]> {
        const response = await this.authorizedFetch(`/threads/${threadId}/state`, { method: "GET" });
        if (!response.ok) {
            throw new Error("Failed to load the conversation");
        }
        return extractMessages(await response.json());
    }

    public async deleteThread(threadId: string): Promise<void> {
        // The deployment's auth layer owner-filters deletes the same way it
        // filters reads, so a user can only delete their own threads.
        const response = await this.authorizedFetch(`/threads/${threadId}`, { method: "DELETE" });
        if (!response.ok) {
            throw new Error("Failed to delete the conversation");
        }
    }

    // Sets a custom title on the thread by patching its metadata. listThreads
    // then prefers metadata.title over the question-derived title. The auth
    // layer owner-filters writes (and re-stamps metadata.owner), so a user can
    // only rename their own threads.
    public async renameThread(threadId: string, title: string): Promise<void> {
        const response = await this.authorizedFetch(`/threads/${threadId}`, {
            method: "PATCH",
            body: JSON.stringify({ metadata: { title } })
        });
        if (!response.ok) {
            throw new Error("Failed to rename the conversation");
        }
    }

    // POSTs the run and consumes the SSE response incrementally. EventSource
    // cannot POST or set headers, so this parses the stream off fetch's
    // ReadableStream. Each "updates" event's data is {"<node-name>": {...}};
    // the UI only needs the node names for progress. The final transcript is
    // re-fetched after the stream ends (server is the source of truth, no
    // fragile delta parsing), so a dropped stream loses only progress labels.
    public async sendMessage(threadId: string, message: string, handlers: StreamHandlers): Promise<void> {
        let response: Response;
        try {
            response = await this.authorizedFetch(`/threads/${threadId}/runs/stream`, {
                method: "POST",
                body: JSON.stringify({
                    assistant_id: this.assistantId,
                    input: { messages: [{ role: "human", content: message }] },
                    stream_mode: ["custom", "updates"]
                })
            });
        } catch (err) {
            handlers.onError(err instanceof Error ? err : new Error("Network error"));
            return;
        }
        if (!response.ok || !response.body) {
            handlers.onError(new Error("Chat is unavailable right now"));
            return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                // SSE frames are separated by a blank line; split on CRLF or LF variants.
                const frames = buffer.split(/\r?\n\r?\n/);
                buffer = frames.pop() ?? "";
                for (const frame of frames) {
                    const { event: eventName, data } = parseSseFrame(frame);
                    if (eventName === "error") {
                        handlers.onError(new Error("The agent hit an error, please try again"));
                        return;
                    }
                    if (eventName === "custom" && data) {
                        try {
                            const payload = JSON.parse(data);
                            if (payload?.type === "thinking" && typeof payload.delta === "string") {
                                handlers.onThinking(String(payload.step ?? ""), payload.delta);
                            }
                        } catch {
                            // Non-JSON custom frames are fine to skip.
                        }
                        continue;
                    }
                    if (eventName !== "updates" || !data) {
                        continue;
                    }
                    try {
                        const payload = JSON.parse(data);
                        if (payload && typeof payload === "object") {
                            for (const nodeName of Object.keys(payload)) {
                                handlers.onNodeUpdate(nodeName);
                            }
                        }
                    } catch {
                        // Non-JSON data frames are fine to skip.
                    }
                }
            }
            // Flush bytes the decoder held back from a multi-byte character split
            // across chunk boundaries. The trailing buffer is intentionally not
            // re-parsed: onDone re-fetches the transcript as the source of truth.
            decoder.decode();
        } catch (err) {
            handlers.onError(err instanceof Error ? err : new Error("Stream error"));
            return;
        } finally {
            // Cancel the reader idempotently: a finished stream ignores cancel, but
            // early returns (error paths above) need it to release the connection.
            reader.cancel().catch(() => {});
        }
        // Awaited so sendMessage's promise covers the caller's completion work
        // (transcript re-fetch); without it the promise resolves mid-handler.
        await handlers.onDone();
    }
}

export const chatDataStore = new ChatDataStore();

// ---- Pure, module-level helpers (no IO/React/state; reused across layers) ----

// Pure helper: one SSE frame's lines -> {event, data}. Handles CRLF, the
// optional space after the field colon, and multi-line data (joined with
// newlines per the SSE spec). Exported for tests.
export const parseSseFrame = (frame: string): SseFrame => {
    let event: string | null = null;
    const dataLines: string[] = [];
    for (const line of frame.split(/\r?\n/)) {
        if (line.startsWith("event:")) {
            event = line.slice("event:".length).replace(/^ /, "").trim();
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).replace(/^ /, ""));
        }
    }
    return { event, data: dataLines.length > 0 ? dataLines.join("\n") : null };
};

// Pure helper: normalize a question into a display title (sidebar entries
// and the conversation heading share this so they always agree).
export const formatThreadTitle = (question: string): string => {
    const text = question.trim().replace(/\s+/g, " ");
    return text.length > 60 ? `${text.slice(0, 57)}...` : text;
};

// Pure helper: sidebar title for a thread, derived from its first question
// (the /threads/search response carries each thread's latest state values,
// so no extra requests or LLM calls are needed). Exported for tests.
export const deriveThreadTitle = (values: unknown): string | null => {
    const firstQuestion = extractMessages({ values })
        .find(m => m.role === "human");
    return firstQuestion ? formatThreadTitle(firstQuestion.content) : null;
};

// Pure helper: LangGraph thread state -> chat transcript. Exported for tests.
export const extractMessages = (state: unknown): ChatMessage[] => {
    const values = (state as { values?: { messages?: unknown } })?.values;
    const rawMessages = values?.messages;
    if (!Array.isArray(rawMessages)) {
        return [];
    }
    const result: ChatMessage[] = [];
    for (const raw of rawMessages) {
        const type = (raw as { type?: unknown })?.type;
        const content = (raw as { content?: unknown })?.content;
        if ((type === "human" || type === "ai") && typeof content === "string") {
            result.push({ role: type, content: content });
        }
    }
    return result;
};
